import './polyfills'
import './bootstrap'
import {createRoot} from 'react-dom/client'
import {createInertiaApp, router, type ResolvedComponent} from '@inertiajs/react'
import React from 'react'
import '@/styles/tailwind.css'
import * as Sentry from '@sentry/react'
import {installDomTranslateGuard} from '@/Utils/domTranslateGuard'
import {installChunkReloadGuard, resolveGlobbedPage} from '@/Utils/chunkReload'
import {shouldForceReplayCapture} from '@/Utils/sentryReplay'
import {preloadPaymentSdk} from '@/Utils/paymentComponent'
import type {PaymentSdkDescriptor} from '@/Types/PaymentComponent'
import StaffAssistantHost from '@/Layouts/StaffAssistantHost'
import ChatHost from '@/Library/Chat/ChatHost'

installDomTranslateGuard()

declare global {
    interface Window {
        version: string
        environment: string
        sentryDsn: string
        pusherAppKey: string
        pusherAppCluster: string
        /** Present only when no Pusher app key is configured, so tests can deliver a channel event. */
        __dispatchLocalPusher?: (channelName: string, event: string, data?: unknown) => void
        pusherChannelTicketViewViewed: string
        pusherChannelTicketViewSecretRotated: string
        pusherTicketCounterSaleEvent: string
        pusherTicketCounterDaysSoldEvent: string
        pusherChannelAgentStats: string
        pusherAgentStatsUpdatedEvent: string
        pusherChannelAssistant: string
        pusherChannelCoachPasses: string
        pusherCoachPassUpdatedEvent: string
        turnstileEnabled: boolean
        turnstileSiteKey: string
        turnstile?: {
            render: (container: string | HTMLElement, options: {
                sitekey: string
                appearance?: 'always' | 'execute' | 'interaction-only'
                size?: 'normal' | 'compact' | 'flexible'
                theme?: 'light' | 'dark' | 'auto'
                action?: string
                callback: (token: string) => void
                'error-callback'?: () => void
                'expired-callback'?: () => void
            }) => string
            reset: (widgetId?: string) => void
            remove: (widgetId?: string) => void
        }
    }
}

if (window.environment !== 'local') {
    Sentry.init({
        dsn: window.sentryDsn,
        enableLogs: true,
        integrations: [
            Sentry.captureConsoleIntegration({levels: ['error']}),
            Sentry.consoleLoggingIntegration({levels: ['log', 'debug', 'info', 'trace', 'warn']}),
            Sentry.browserTracingIntegration(),
            Sentry.browserProfilingIntegration(),
            Sentry.replayIntegration({
                maskAllText: false,
                maskAllInputs: false,
                blockAllMedia: false
            })
        ],
        environment: window.environment,
        release: window.version,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        ignoreErrors: ['HttpNetworkError'],
    })
}

function ensureReplayCapture(component: string): void {
    if (!shouldForceReplayCapture(component)) {
        return
    }

    const replay = Sentry.getReplay()

    if (replay != null && replay.getRecordingMode() !== 'session') {
        replay.start()
    }
}

router.on('httpException', event => {
    Sentry.captureException(
        new Error(`Inertia httpException ${event.detail.response?.status ?? 'unknown'} at ${window.location.pathname}`),
        {extra: {response: event.detail.response}},
    )
})

// The marketing site's pages belong to marketing.tsx (ENG-4726).
// A colocated *.test.tsx matches **/*.tsx. Vapor's build copy omits tests/, so that file is
// built as a page and an import of tests/Jest fails production (MessageBadges.test.tsx).
const pages = import.meta.glob<{default: ResolvedComponent}>([
    './Pages/**/*.tsx',
    '!./Pages/Marketing/**',
    '!./Pages/**/*.test.tsx',
])

installChunkReloadGuard(router)

// Inertia fires `navigate` for the initial page as well as every visit. A
// page whose controller sends `payment_sdk` mints its payment descriptor
// only on the pay step, so its processor's SDK is fetched here, with the
// page, instead of after the charge-intent round trip (ENG-4688).
router.on('navigate', event => {
    ensureReplayCapture(event.detail.page.component)
    preloadPaymentSdk(event.detail.page.props.payment_sdk as PaymentSdkDescriptor | null | undefined)
})

void createInertiaApp({
    resolve: (name) => resolveGlobbedPage(pages, name),
    setup({
              el,
              App,
              props
          }) {
        ensureReplayCapture(props.initialPage.component)

        createRoot(el).render(
            <Sentry.ErrorBoundary fallback={<>
                <div className={'flex flex-col items-center justify-center h-screen'}>
                    <div>
                        An error occurred while building this page.
                    </div>
                    <div className={'mt-4'}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
                             className="size-5">
                            <path fillRule="evenodd"
                                  d="M19 5.5a4.5 4.5 0 0 1-4.791 4.49c-.873-.055-1.808.128-2.368.8l-6.024 7.23a2.724 2.724 0 1 1-3.837-3.837L9.21 8.16c.672-.56.855-1.495.8-2.368a4.5 4.5 0 0 1 5.873-4.575c.324.105.39.51.15.752L13.34 4.66a.455.455 0 0 0-.11.494 3.01 3.01 0 0 0 1.617 1.617c.17.07.363.02.493-.111l2.692-2.692c.241-.241.647-.174.752.15.14.435.216.9.216 1.382ZM4 17a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                                  clipRule="evenodd"/>
                        </svg>
                    </div>
                </div>
            </>}>
                <App {...props} />
                <StaffAssistantHost initialPage={props.initialPage}/>
                <ChatHost/>
            </Sentry.ErrorBoundary>
        )
    },
    progress: {
        color: '#4B5563'
    }
})
